// Pair Plotの設定UIコンポーネント - タブ式UI

import { useState } from 'react';
import { GraphConfigProps, PairPlotConfig } from '../../../../types/graph-builder.types';
import { ColumnId } from '../../../../types';
import { getIntervalRatioColumns } from '../../../utils/graph-builder-utils';
import * as styles from '../../../styles/components/graph-builder.css.ts';

export function PairPlotConfigComponent({
  config,
  dataset,
  onChange
}: GraphConfigProps<PairPlotConfig>) {
  const [activeTab, setActiveTab] = useState<'variables' | 'order'>('variables');

  // interval/ratio尺度の数値列のみを取得
  const numericColumns = getIntervalRatioColumns(dataset);

  const handleColumnToggle = (columnId: ColumnId) => {
    const currentColumns = config.columns || [];
    const isSelected = currentColumns.includes(columnId);

    if (isSelected) {
      // 選択解除
      onChange({
        ...config,
        columns: currentColumns.filter(id => id !== columnId)
      });
    } else {
      // 選択追加
      onChange({
        ...config,
        columns: [...currentColumns, columnId]
      });
    }
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newColumns: ColumnId[] = [...(config.columns || [])];
    [newColumns[index - 1], newColumns[index]] = [newColumns[index], newColumns[index - 1]];
    onChange({
      ...config,
      columns: newColumns
    });
  };

  const handleMoveDown = (index: number) => {
    const columns = config.columns || [];
    if (index === columns.length - 1) return;
    const newColumns: ColumnId[] = [...columns];
    [newColumns[index], newColumns[index + 1]] = [newColumns[index + 1], newColumns[index]];
    onChange({
      ...config,
      columns: newColumns
    });
  };

  const selectedColumns = config.columns || [];
  const selectedColumnNames = selectedColumns.map(colId => {
    const col = dataset.schema.columns.find(c => c.id === colId);
    return col ? col.name : colId;
  });

  return (
    <div className={styles.tabContainer}>
      {/* タブヘッダー */}
      <div className={styles.tabHeader}>
        <button
          className={`${styles.tab} ${activeTab === 'variables' ? styles.active : ''}`}
          onClick={() => setActiveTab('variables')}
        >
          Variables
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'order' ? styles.active : ''}`}
          onClick={() => setActiveTab('order')}
          disabled={selectedColumns.length === 0}
        >
          Display Order {selectedColumns.length > 0 && `(${selectedColumns.length})`}
        </button>
      </div>

      {/* タブコンテンツ */}
      <div className={styles.tabContent}>
        {/* Variables Tab */}
        {activeTab === 'variables' && (
          <div>
            <div className={styles.settingItem}>
              <div className={styles.hint}>
                Select 2-6 numeric variables to display in the pair plot matrix.
              </div>

              {/* 利用可能な列のチェックボックスリスト */}
              <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px', padding: '8px', marginTop: '8px' }}>
                {numericColumns.length === 0 ? (
                  <div style={{ color: '#6c757d', fontSize: '12px' }}>No numeric columns with interval/ratio scale</div>
                ) : (
                  numericColumns.map(col => (
                    <label key={col.id} className={styles.settingCheckbox} style={{ display: 'block', marginBottom: '4px' }}>
                      <input
                        type="checkbox"
                        checked={selectedColumns.includes(col.id)}
                        onChange={() => handleColumnToggle(col.id)}
                      />
                      {col.name} {col.scale?.scale ? `(${col.scale.scale})` : ''}
                    </label>
                  ))
                )}
              </div>
            </div>

            {/* 警告メッセージ */}
            {selectedColumns.length > 6 && (
              <div style={{
                marginTop: '8px',
                padding: '8px',
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                borderRadius: '4px',
                fontSize: '12px',
                color: '#856404'
              }}>
                ⚠️ Warning: Selecting more than 6 variables may impact performance and readability.
              </div>
            )}

            {selectedColumns.length > 0 && (
              <div className={styles.hint} style={{ marginTop: '8px' }}>
                Switch to the "Display Order" tab to rearrange variables.
              </div>
            )}
          </div>
        )}

        {/* Display Order Tab */}
        {activeTab === 'order' && (
          <div>
            {selectedColumns.length === 0 ? (
              <div className={styles.hint}>
                Select variables in the "Variables" tab first.
              </div>
            ) : (
              <div className={styles.settingItem}>
                <div className={styles.hint}>
                  Variables are displayed in this order (top to bottom, left to right).
                </div>

                <div style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '8px', marginTop: '8px' }}>
                  {selectedColumnNames.map((name, index) => (
                    <div key={selectedColumns[index]} style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '4px',
                      padding: '4px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '3px'
                    }}>
                      <span style={{ flex: 1, fontSize: '13px' }}>{index + 1}. {name}</span>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button
                          type="button"
                          onClick={() => handleMoveUp(index)}
                          disabled={index === 0}
                          style={{
                            padding: '2px 8px',
                            fontSize: '11px',
                            border: '1px solid #ccc',
                            borderRadius: '3px',
                            backgroundColor: '#fff',
                            cursor: index === 0 ? 'not-allowed' : 'pointer',
                            opacity: index === 0 ? 0.5 : 1
                          }}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMoveDown(index)}
                          disabled={index === selectedColumns.length - 1}
                          style={{
                            padding: '2px 8px',
                            fontSize: '11px',
                            border: '1px solid #ccc',
                            borderRadius: '3px',
                            backgroundColor: '#fff',
                            cursor: index === selectedColumns.length - 1 ? 'not-allowed' : 'pointer',
                            opacity: index === selectedColumns.length - 1 ? 0.5 : 1
                          }}
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          onClick={() => handleColumnToggle(selectedColumns[index])}
                          style={{
                            padding: '2px 8px',
                            fontSize: '11px',
                            border: '1px solid #dc3545',
                            borderRadius: '3px',
                            backgroundColor: '#fff',
                            color: '#dc3545',
                            cursor: 'pointer'
                          }}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
